/**
 * Local Network Access permission state.
 *
 * Chrome 142+ gates requests to `.local` names, RFC1918 addresses and loopback
 * behind a Local Network Access permission prompt. Our gateway claim beacon
 * (`https://claim-<code>.local`) is exactly such a request, so firing one
 * unprompted raises a browser permission dialog with no context — and a
 * dismissal is sticky per-origin.
 *
 * Querying the permission never raises the prompt, so we can check first and
 * only beacon when we know what will happen. See:
 * https://developer.chrome.com/blog/local-network-access
 */

import { useCallback, useEffect, useState } from 'react';

export type LocalNetworkPermissionState =
  | 'checking'
  /** Already allowed — safe to beacon without any user action. */
  | 'granted'
  /** Not decided yet. Beaconing will raise the browser prompt, so require a click. */
  | 'prompt'
  /** Blocked for this origin. Only site settings can undo it. */
  | 'denied'
  /** Browser has no such permission (Safari, Firefox today). Beacons just work. */
  | 'unsupported';

/**
 * `local-network-access` is the back-compat alias; the spec later split into
 * `local-network` (RFC1918 + `.local`) and `loopback-network`. We only ever
 * talk to `.local`, so the first two are the ones worth asking about.
 */
const PERMISSION_NAMES = ['local-network-access', 'local-network'];

/**
 * Resolve a permission name this browser actually recognises.
 *
 * `permissions.query` rejects with a TypeError for names it doesn't know, which
 * is our feature-detection signal — there's no capability flag to read.
 */
async function queryFirstSupported(): Promise<PermissionStatus | null> {
  // lib.dom types `navigator.permissions` as always present; older Safari
  // disagrees, so ask the optional view of it.
  const { permissions } = navigator as Partial<Navigator>;
  if (!permissions) {
    return null;
  }

  for (const name of PERMISSION_NAMES) {
    try {
      // Cast: these names aren't in lib.dom's PermissionName union yet.
      return await permissions.query({ name: name as PermissionName });
    } catch {
      // Unknown name in this browser — try the next spelling.
    }
  }

  return null;
}

export interface UseLocalNetworkPermissionResult {
  state: LocalNetworkPermissionState;
  /** True when beaconing needs a user gesture first (i.e. a prompt may appear). */
  needsPrompt: boolean;
  /** True when beaconing is safe to start on our own. */
  canScanSilently: boolean;
}

/**
 * Track the Local Network Access permission, live.
 *
 * Subscribes to `onchange` so flipping the toggle in site settings updates the
 * UI without a reload — which is what lets the blocked state recover in place
 * and what lets the background watcher start the moment a user clicks Allow.
 */
export function useLocalNetworkPermission(): UseLocalNetworkPermissionResult {
  const [state, setState] = useState<LocalNetworkPermissionState>('checking');

  const apply = useCallback((status: PermissionStatus) => {
    setState(status.state);
  }, []);

  useEffect(() => {
    const unmounted = new AbortController();
    let status: PermissionStatus | null = null;
    const onChange = () => {
      if (status) apply(status);
    };

    void (async () => {
      const result = await queryFirstSupported();
      if (unmounted.signal.aborted) return;

      if (!result) {
        setState('unsupported');
        return;
      }

      status = result;
      apply(result);
      // Tied to the same signal, so unmount detaches it for us.
      result.addEventListener('change', onChange, { signal: unmounted.signal });
    })();

    return () => { unmounted.abort(); };
  }, [apply]);

  return {
    state,
    // 'unsupported' deliberately counts as needing a gesture: Safari and iOS
    // raise an OS-level local network prompt of their own, and an unexpected
    // burst of multicast is bad manners regardless of who's asking.
    needsPrompt: state === 'prompt' || state === 'unsupported',
    canScanSilently: state === 'granted',
  };
}
